<script setup lang="ts">
import { computed, ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import EmptyState from '@uiKit/components/EmptyState.vue'
import ActivityRow from './ActivityRow.vue'
import { groupTransactionsByMonth } from '../utils/transactionGroupUtils'
import { useInfiniteScrollObserver } from '@shared/composables/useInfiniteScrollObserver'
import type { Transaction } from '../types/transactionTypes'

interface Props {
  transactions: Transaction[]
  loading?: boolean
  loadingMore?: boolean
  hasMore?: boolean
  /** Eyebrow above the card. Empty string hides the header row entirely. */
  title?: string
  /** Route for the "See All" link. Omit to hide the link entirely. */
  seeAllTo?: string
  /**
   * The list is showing a filtered view with no matches, rather than a wallet
   * that has never had activity — swaps the empty state and drops the CTA.
   */
  isFiltered?: boolean
  /** Copy for the "nothing here yet" state — token activity words it differently. */
  emptyTitle?: string
  emptyDescription?: string
  /** Omit to render the empty state without a call to action. */
  emptyCtaLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  loadingMore: false,
  hasMore: false,
  title: 'Recent Transactions',
  seeAllTo: '',
  isFiltered: false,
  emptyTitle: 'No transactions yet',
  emptyDescription: 'Add funds to your wallet and your activity will show up here.',
  emptyCtaLabel: 'Add funds',
})

const emit = defineEmits<{
  (e: 'load-more'): void
  (e: 'view-details', tx: Transaction): void
  (e: 'add-funds'): void
}>()

const SKELETON_ROWS = 5

const groups = computed(() => groupTransactionsByMonth(props.transactions))

// Raw <div ref> — IntersectionObserver targets DOM nodes. §19 exception.
const sentinelRef = ref<HTMLElement | null>(null)

useInfiniteScrollObserver(sentinelRef, {
  canLoadMore: () => Boolean(props.hasMore) && !props.loadingMore && !props.loading,
  onLoadMore: () => emit('load-more'),
})
</script>

<template>
  <Box direction="col" gap="3" class="wtc-wrap">
    <Box
      v-if="title || seeAllTo || $slots.controls"
      align="center"
      justify="between"
      gap="3"
      class="wtc-wrap__head"
    >
      <Text v-if="title" type="body" weight="bold" color="muted" transform="uppercase" is="span">
        {{ title }}
      </Text>

      <Box align="center" gap="3" class="wtc-wrap__actions">
        <slot name="controls" />
        <RouterLink v-if="seeAllTo" :to="seeAllTo" class="wtc-wrap__see-all">
          <Text type="body" weight="medium" color="primary" is="span">See All</Text>
        </RouterLink>
      </Box>
    </Box>

    <Box direction="col" is="section" class="wtc">
      <!-- Skeleton mirrors the row grid exactly so nothing shifts on load. -->
      <template v-if="loading">
        <Box v-for="i in SKELETON_ROWS" :key="i" align="center" gap="4" class="wtc__skeleton-row">
          <Skeleton animation="wave" size="46px" shape="circle" />
          <Box direction="col" gap="1" grow>
            <Skeleton animation="wave" width="180px" height="16px" border-radius="4px" />
            <Skeleton animation="wave" width="130px" height="14px" border-radius="4px" />
          </Box>
          <Box class="wtc__skeleton-status">
            <Skeleton animation="wave" width="70px" height="14px" border-radius="4px" />
          </Box>
          <Box justify="end" class="wtc__skeleton-amount">
            <Skeleton animation="wave" width="96px" height="16px" border-radius="4px" />
          </Box>
        </Box>
      </template>

      <EmptyState
        v-else-if="!transactions.length && isFiltered"
        class="wtc__empty"
        size="compact"
        icon="search"
        title="No transactions match your filters"
        description="Try clearing a filter or widening the date range."
      />

      <EmptyState
        v-else-if="!transactions.length"
        class="wtc__empty"
        size="compact"
        icon="list"
        :title="emptyTitle"
        :description="emptyDescription"
        :primary-action-label="emptyCtaLabel || undefined"
        @primary-action="emit('add-funds')"
      />

      <template v-else>
        <template v-for="group in groups" :key="group.key">
          <Box class="wtc__month">
            <Text type="body" weight="bold" color="muted" transform="uppercase" is="span">
              {{ group.label }}
            </Text>
          </Box>
          <ActivityRow
            v-for="tx in group.transactions"
            :key="tx.id"
            :transaction="tx"
            @view-details="(row) => emit('view-details', row)"
          />
        </template>

        <!-- Raw <div ref> for IntersectionObserver target. §19 exception. -->
        <div v-if="hasMore || loadingMore" ref="sentinelRef" class="wtc__sentinel">
          <Box v-if="loadingMore" align="center" justify="center" gap="2" class="wtc__more">
            <PiIcon name="spinner" class="wtc__more-icon" />
            <Text type="body-sm" color="muted" is="span">Loading more…</Text>
          </Box>
        </div>
      </template>
    </Box>
  </Box>
</template>

<style scoped>
.wtc-wrap__head {
  min-height: 20px;
}

.wtc-wrap__actions {
  flex-shrink: 0;
}

/* Design draws See All as an underlined violet link, not a button. */
.wtc-wrap__see-all {
  text-decoration: underline;
  text-underline-offset: 2px;
  border-radius: 4px;
}

.wtc-wrap__see-all:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.wtc {
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: 16px;
  padding: 0.25rem 1.5rem 0.5rem;
  box-sizing: border-box;
}

.wtc__month {
  padding: 1.125rem 0 0.625rem;
}

.wtc__skeleton-row {
  padding: 1rem 0;
  min-height: 79px;
  box-sizing: border-box;
}

.wtc__skeleton-row + .wtc__skeleton-row {
  border-top: 1px solid var(--p-content-border-color);
}

.wtc__skeleton-status {
  width: 140px;
  flex-shrink: 0;
}

.wtc__skeleton-amount {
  width: 180px;
  flex-shrink: 0;
}

.wtc__empty {
  padding: 2rem 0;
}

.wtc__sentinel {
  min-height: 1px;
}

.wtc__more {
  padding: 1rem 0;
}

.wtc__more-icon {
  font-size: 16px;
  color: var(--p-text-muted-color);
  animation: wtc-spin 0.8s linear infinite;
}

@keyframes wtc-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 992px) {
  .wtc {
    padding: 0.25rem 1rem 0.5rem;
  }

  /* Match the row's folded mobile shape so the swap doesn't jump. */
  .wtc__skeleton-row {
    flex-wrap: wrap;
    row-gap: 0.5rem;
  }

  .wtc__skeleton-status,
  .wtc__skeleton-amount {
    width: auto;
  }
}
</style>
